import { Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React, { useContext, useMemo, useState } from 'react';

import { useConditionsQuery, useStripeIntentQuery } from '../../hooks/useQueries';

import { stripeElementsOptions } from './stripeElementsOptions';

import type { RuntimeConfiguration } from '../../../types/config';
import type { PaymentWidgetData } from '../../models/queries';
import type { PaymentIntent, SetupIntent, StripeError } from '@stripe/stripe-js';

const assertEnv = (variableName: keyof RuntimeConfiguration) => {
  if (typeof window.insify_runtime_config[variableName] !== 'string') {
    throw new Error(`${variableName} is not defined`);
  }
};

type PaymentStateInitializing = {
  state: 'initializing';
};

type PaymentStateIdle = {
  state: 'idle';
};

type PaymentStateLoading = {
  state: 'loading';
};

type PaymentStateSuccess = {
  state: 'success';
  data: PaymentIntent | SetupIntent;
};

type PaymentStateError = {
  state: 'error';
  data: StripeError;
};

type PaymentState =
  | PaymentStateInitializing
  | PaymentStateIdle
  | PaymentStateLoading
  | PaymentStateSuccess
  | PaymentStateError;

type StripeContext = {
  paymentState: PaymentState;
  setPaymentState?: React.Dispatch<React.SetStateAction<PaymentState>>;
  confirmStripeIntent?: (intent: PaymentWidgetData) => Promise<void>;
};

const stripeContext = React.createContext<StripeContext | null>(null);

const StripeProvider: React.FC<{ children: React.ReactNode }> = ({ children }): JSX.Element => {
  const [paymentState, setPaymentState] = useState<PaymentState>({ state: 'idle' });
  const stripe = useStripe();
  const elements = useElements();

  const confirmStripeIntent = async (intent: PaymentWidgetData) => {
    if (!stripe || !elements) throw new Error('Stripe instance or Stripe Elements are not detected.');

    setPaymentState({ state: 'loading' });

    switch (intent.intentType) {
      case 'setup-intent': {
        let { setupIntent, error } = await stripe.retrieveSetupIntent(intent.stripeClientSecret);
        if (setupIntent?.status !== 'succeeded') {
          const result = await stripe.confirmSetup({ elements, redirect: 'if_required' });
          setupIntent = result.setupIntent;
          error = result.error;
        }
        if (setupIntent) {
          return setPaymentState({ state: 'success', data: setupIntent });
        }
        if (error) {
          return setPaymentState({ state: 'error', data: error });
        }
        break;
      }
      case 'payment-intent': {
        let { paymentIntent, error } = await stripe.retrievePaymentIntent(intent.stripeClientSecret);
        if (paymentIntent?.status !== 'succeeded') {
          const result = await stripe.confirmPayment({ elements, redirect: 'if_required' });
          paymentIntent = result.paymentIntent;
          error = result.error;
        }
        if (paymentIntent) {
          return setPaymentState({ state: 'success', data: paymentIntent });
        }
        if (error) {
          return setPaymentState({ state: 'error', data: error });
        }
        break;
      }
    }

    return setPaymentState({ state: 'idle' });
  };

  return (
    <stripeContext.Provider value={{ confirmStripeIntent, paymentState, setPaymentState }}>
      {children}
    </stripeContext.Provider>
  );
};

export const useStripePayment = (): StripeContext => {
  const context = useContext(stripeContext);

  if (context === undefined) {
    throw new Error('useStripePayment must be used within a StripeProvider');
  }

  if (context === null) {
    return {
      paymentState: {
        state: 'initializing',
      },
    };
  }

  return context;
};

const useStripeConfiguration = () => {
  assertEnv('STRIPE_PUBLIC_KEY_MRE');
  assertEnv('STRIPE_PUBLIC_KEY_WAKAM');

  const stripePublicKeyMre = window.insify_runtime_config.STRIPE_PUBLIC_KEY_MRE;
  const stripePublicKeyWakamOrHiscox = window.insify_runtime_config.STRIPE_PUBLIC_KEY_WAKAM;

  return { stripePublicKeyMre, stripePublicKeyWakamOrHiscox };
};

export const StripeElementsProvider: React.FC<{ children: React.ReactNode }> = ({ children }): JSX.Element => {
  const { stripePublicKeyMre, stripePublicKeyWakamOrHiscox } = useStripeConfiguration();

  const { data: stripeIntent, isStale: isStripeIntentStale } = useStripeIntentQuery();
  const { data: conditions } = useConditionsQuery();
  const riskCarriers = conditions?.riskCarriers || [];
  const stripePublicKey = ['munichRe', 'axeria'].includes(riskCarriers[0])
    ? stripePublicKeyMre
    : stripePublicKeyWakamOrHiscox;

  const stripePromise = loadStripe(stripePublicKey);

  const Component = useMemo(() => {
    if (!isStripeIntentStale && stripeIntent?.stripeClientSecret) {
      return (
        <Elements
          stripe={stripePromise}
          options={{
            ...stripeElementsOptions,
            clientSecret: stripeIntent.stripeClientSecret,
          }}
        >
          <StripeProvider>{children}</StripeProvider>
        </Elements>
      );
    }

    return <>{children}</>;
  }, [stripeIntent]);

  return Component;
};
